import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Layout } from "@/components/site/Layout";
import { listProductsPublic, getPublicSettings } from "@/lib/content.functions";
import { ArrowRight, Loader2 } from "lucide-react";
import { useT } from "@/lib/i18n";

function formatPrice(cents: number, currency: string, lang: string) {
  const locale = lang === "pt" ? "pt-BR" : lang === "en" ? "en-GB" : "es-ES";
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currency.toUpperCase(),
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

export const Route = createFileRoute("/services/paquetes")({
  component: Packages,
  head: () => ({
    meta: [
      { title: "Fixed-scope packages — PULSO33" },
      { name: "description", content: "Concrete IT engagements with defined scope and a fixed price. No surprises, guided process." },
      { property: "og:title", content: "Fixed-scope packages — PULSO33" },
      { property: "og:description", content: "Engagements with defined scope and a single price." },
      { property: "og:url", content: "https://pulso33.com/services/paquetes" },
    ],
    links: [{ rel: "canonical", href: "https://pulso33.com/services/paquetes" }],
  }),
});

function Packages() {
  const { tk, lang } = useT();
  const list = useServerFn(listProductsPublic);
  const getSettings = useServerFn(getPublicSettings);
  const { data, isLoading } = useQuery({
    queryKey: ["products-public"],
    queryFn: () => list(),
  });
  const { data: settingsData } = useQuery({
    queryKey: ["public-settings"],
    queryFn: () => getSettings(),
  });
  const products = (data?.products ?? []).filter((p) => p.show_in_catalog !== false);
  const consultationEnabled = settingsData?.settings.consultation_payment_enabled ?? true;

  return (
    <Layout>
      <section>
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="text-center mb-12">
            <span className="text-[10px] tracking-[0.4em] uppercase text-gold">{tk("packages.kicker", "Packages")}</span>
            <h1 className="font-serif text-4xl md:text-5xl mt-4">
              {tk("packages.title.a", "Fixed scope, ")}
              <em className="not-italic gold-gradient">{tk("packages.title.b", "fixed price")}</em>
            </h1>
            <p className="mt-5 text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              {tk("packages.intro", "Engagements with a defined deliverable and a single price. No surprises, with hands-on follow-up.")}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {isLoading && (
              <div className="col-span-full flex justify-center py-12 text-muted-foreground">
                <Loader2 className="animate-spin" />
              </div>
            )}
            {consultationEnabled && (
              <div className="rounded-2xl border border-gold/30 bg-card/30 p-8 flex flex-col">
                <div className="text-[10px] tracking-[0.3em] uppercase text-gold/70">{tk("packages.consult.kicker", "Consultation")}</div>
                <h3 className="font-serif text-2xl mt-3">{tk("packages.consult.title", "45-min discovery call")}</h3>
                <p className="text-sm text-muted-foreground mt-4 leading-relaxed flex-1">
                  {tk("packages.consult.desc", "A focused session to scope your idea, map the architecture and define the smallest next step. Immediate email confirmation.")}
                </p>
                <Link
                  to="/contact"
                  className="mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-gold text-primary-foreground px-6 py-3 text-xs tracking-[0.3em] uppercase hover:bg-[oklch(0.88_0.08_85)] transition-colors"
                >
                  {tk("packages.consult.cta", "Book a call")} <ArrowRight size={12} />
                </Link>
              </div>
            )}
            {products.map((p) => (
              <div
                key={p.slug}
                className="rounded-2xl border border-gold/20 bg-card/30 p-8 flex flex-col"
              >
                <div className="text-[10px] tracking-[0.3em] uppercase text-gold/70">{tk("packages.pkg", "Package")}</div>
                <h3 className="font-serif text-2xl mt-3">{tk(`products.${p.slug}.name`, p.name)}</h3>
                <div className="text-3xl gold-gradient font-serif mt-4">
                  {formatPrice(p.price_cents, p.currency, lang)}
                </div>
                <p className="text-sm text-muted-foreground mt-4 leading-relaxed flex-1">
                  {tk(`products.${p.slug}.description`, p.description)}
                </p>
                <Link
                  to="/contact"
                  className="mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-gold text-primary-foreground px-6 py-3 text-xs tracking-[0.3em] uppercase hover:bg-[oklch(0.88_0.08_85)] transition-colors"
                >
                  {tk("packages.cta", "Get started")} <ArrowRight size={12} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
}
